import type { BoardLayout } from './layout';

/** A tile's place on the board, in px. */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Which snapshot the board shows: a new id each time one is taken, and whether Refresh took it. */
export interface BoardSnapshot {
  id: number;
  refreshed: boolean;
}

/** What the board showed, as far as its animations care. */
export interface BoardFrame {
  rects: ReadonlyMap<string, Rect>;
  crown: string | null;
  /** The board's area in px², so sizes compare across a resize. */
  area: number;
}

/** What pressing Refresh changed, for the board to show. */
export interface BoardChanges {
  /** Posts that weren't on the board before. */
  added: string[];
  /** Posts whose share of the board grew by `RISING_GROWTH` or more (never the talk of the town). */
  rising: string[];
  /** The new talk of the town, when it changed. */
  crowned: string | null;
  /** Posts that left the board (they fade where they were). */
  left: { id: string; from: Rect }[];
}

/** A post rises when a refresh grows its share of the board by at least this factor. */
export const RISING_GROWTH = 1.6;

export function frameOf(layout: BoardLayout, width: number, height: number): BoardFrame {
  return {
    rects: new Map(layout.tiles.map((t) => [t.post.id, { x: t.x, y: t.y, w: t.w, h: t.h }])),
    crown: layout.crown,
    area: width * height,
  };
}

/** Compares the board before and after a refresh. */
export function boardChanges(prev: BoardFrame, next: BoardFrame): BoardChanges {
  const share = (r: Rect, area: number) => (area > 0 ? (r.w * r.h) / area : 0);
  const added: string[] = [];
  const rising: string[] = [];
  for (const [id, r] of next.rects) {
    const before = prev.rects.get(id);
    if (!before) added.push(id);
    else if (id !== next.crown && share(r, next.area) >= share(before, prev.area) * RISING_GROWTH) {
      rising.push(id);
    }
  }
  const left = [...prev.rects]
    .filter(([id]) => !next.rects.has(id))
    .map(([id, from]) => ({ id, from }));
  return {
    added,
    rising,
    crowned: next.crown && next.crown !== prev.crown ? next.crown : null,
    left,
  };
}
